import React, { useEffect, useState } from 'react';
import { biGet, errText } from './bi/biApi';
import { Card, Kpi, Empty, Loading } from './bi/BiUi';
import { COLORS, AttendancePie, MultiLine, VBars } from './bi/DashCharts';

// Student / Parent dashboard: personal attendance pie, subject marks vs class average, monthly homework line.
// Data: GET /api/analytics/student/:classId/:id and /api/analytics/student-insights/:classId/:id
export default function MyPerformanceCard({ user }) {
  const classId = user?.class || user?.assignedClass || '';
  const studentId = user?.id || user?._id || '';
  const [data, setData] = useState(null);
  const [ins, setIns] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!classId || !studentId) { setLoading(false); setErr('Class or student id missing'); return undefined; }
    let live = true;
    setLoading(true); setErr('');
    const base = `/api/analytics/student`;
    const ids = `${encodeURIComponent(classId)}/${encodeURIComponent(studentId)}`;
    const u = { ...user, role: user?.role || 'student' };
    Promise.all([
      biGet(u, `${base}/${ids}`),
      biGet(u, `/api/analytics/student-insights/${ids}`).catch(() => null)
    ]).then(([d, i]) => {
      if (!live) return;
      if (d?.error) setErr(d.error); else setData(d);
      setIns(i);
    }).catch((e) => live && setErr(errText(e))).finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [classId, studentId, user]);

  const att = data?.attendance;
  const hw = data?.homework;
  const marks = ins?.subjects || [];
  const monthly = (ins?.monthlyHomework || []).filter((m) => m.homeworkPct != null);

  return (
    <div id="performance" style={{ gridColumn: 'span 2' }}>
      <div className="caf-theme-wrapper rounded-2xl bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 p-4 text-slate-100">
        <h3 className="mb-3 text-lg font-bold text-white">📈 My Performance</h3>
        {loading && <Loading />}
        {err && !loading && <p className="text-sm text-rose-300">{err}</p>}
        {data && att && hw && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Kpi label="Attendance" value={`${att.pct}%`} tone={att.meetsBoardCriteria === false ? 'rose' : 'emerald'} icon="📋" hint={`${att.present}/${att.workingDays} days present`} />
              <Kpi label="Absent" value={att.absent} tone="rose" icon="🔴" hint="days" />
              <Kpi label="Sanctioned leave" value={att.leave ?? 0} tone="amber" icon="🟡" hint="days" />
              <Kpi label="Homework discipline" value={`${hw.disciplinePct}%`} tone="indigo" icon="📝" hint={`${hw.pending} pending day(s)`} />
            </div>
            {att.meetsBoardCriteria === false && <p className="text-xs text-rose-300">Attendance is below the 75% board criteria.</p>}
            <div className="grid gap-4 lg:grid-cols-2">
              <Card title="My Attendance" subtitle="Cumulative this academic year">
                {att.present + att.absent + (att.leave || 0) > 0
                  ? <AttendancePie present={att.present} absent={att.absent} leave={att.leave || 0} centerLabel={`${att.pct}%`} />
                  : <Empty>No attendance recorded yet.</Empty>}
              </Card>
              <Card title="Marks vs Class Average" subtitle={ins?.examLabel ? `${ins.examLabel} (percentage)` : 'Latest exam'}>
                {marks.length
                  ? <VBars data={marks} xKey="subject" bars={[
                    { key: 'marksPct', name: 'My marks', color: COLORS.academic },
                    { key: 'classAvgPct', name: 'Class average', color: COLORS.leave }]} />
                  : <Empty>No exam marks published yet.</Empty>}
              </Card>
              <Card title="Monthly Homework Consistency" subtitle="Share of present days with homework completed" className="lg:col-span-2">
                {monthly.length
                  ? <MultiLine data={monthly} xKey="month" xFormat={(m) => m} series={[{ key: 'homeworkPct', name: 'Homework completed', color: COLORS.academic }]} />
                  : <Empty>No monthly homework data yet.</Empty>}
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
